import React, { useState, useEffect, useRef } from 'react';
import AdminNavbar from '../../component/adminNavbar';
import {
	Box,
	Heading,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Input,
	Button,
	HStack,
	Select,
	Tooltip,
	Badge,
	ButtonGroup,
	Text,
	Spinner,
	SimpleGrid,
	Flex,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	Stack,
	Divider,
	Center,
} from '@chakra-ui/react';
import { getProducts, importProducts, deactivateProduct } from '../../redux/actions/productActions';
import { connect } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	useReactTable,
	createColumnHelper,
	flexRender,
	getCoreRowModel,
} from '@tanstack/react-table';
import { EditIcon, DeleteIcon, AddIcon, SearchIcon, ArrowUpIcon, TriangleUpIcon, TriangleDownIcon, UpDownIcon } from '@chakra-ui/icons';
import { MdAttachMoney, MdInventory } from 'react-icons/md';
import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
const authConfig = () => ({ headers: { 'x-auth-token': localStorage.getItem('x-auth-token') } });
import Alert from '../../component/alert';

const Products = ({ getProducts, adminProducts, loading, count, importProducts, deactivateProduct }) => {
	const [searchParams, setSearchParams] = useSearchParams();

	const nameFilter = searchParams.get('name') || '';
	const statusFilter = searchParams.get('status') || '';
	const fbFilter = searchParams.get('fb') || '';
	const categoryFilter = searchParams.get('category') || '';
	const stockFilter = searchParams.get('stock') || '';
	const sortFilter = searchParams.get('sort') || 'newest';
	const offset = parseInt(searchParams.get('offset') || '0', 10);

	const setNameFilter = (val: string) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set('name', val); return n; }, { replace: true });
	const setStatusFilter = (val: string) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set('status', val); return n; }, { replace: true });
	const setFbFilter = (val: string) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set('fb', val); return n; }, { replace: true });
	const setCategoryFilter = (val: string) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set('category', val); return n; }, { replace: true });
	const setStockFilter = (val: string) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set('stock', val); return n; }, { replace: true });
	const setSortFilter = (val: string) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set('sort', val); n.set('offset', '0'); return n; }, { replace: true });
	const setOffset = (val: number) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set('offset', String(val)); return n; }, { replace: true });

	const [categories, setCategories] = useState<any[]>([]);
	useEffect(() => {
		axios.get(`${BASE_URL}/api/get-categories`, authConfig()).then((res) => setCategories(res.data || []));
	}, []);

	const [isSearching, setIsSearching] = useState(false);
	const [isImporting, setIsImporting] = useState(false);
	const [importSummary, setImportSummary] = useState<{ added: number; failed: number; errors: string[] } | null>(null);
	const [priceCheckProduct, setPriceCheckProduct] = useState<any>(null);
	const [priceCheckData, setPriceCheckData] = useState<any[]>([]);
	const [priceCheckLoading, setPriceCheckLoading] = useState(false);
	const [stockCheckProduct, setStockCheckProduct] = useState<any>(null);
	const [stockCheckData, setStockCheckData] = useState<any[]>([]);
	const [stockCheckLoading, setStockCheckLoading] = useState(false);

	const navigate = useNavigate();

	const handlePriceCheck = async (row) => {
		setPriceCheckProduct(row.original);
		setPriceCheckData([]);
		setPriceCheckLoading(true);
		try {
			const res = await axios.get(`${BASE_URL}/api/products/${row.original.product_id}/pricing-preview`, authConfig());
			setPriceCheckData(res.data || []);
		} finally {
			setPriceCheckLoading(false);
		}
	};
	const handleStockCheck = async (row) => {
		setStockCheckProduct(row.original);
		setStockCheckData([]);
		setStockCheckLoading(true);
		try {
			const res = await axios.get(`${BASE_URL}/api/products/${row.original.product_id}/variant-stock`, authConfig());
			setStockCheckData(res.data || []);
		} finally {
			setStockCheckLoading(false);
		}
	};

	const fileInputRef = useRef(null);

	const createProd = () => navigate('create-product');
	const editProduct = (row) => navigate(`edit-product/${row.original.product_id}`);
	const handleDeactivate = async (row) => {
		if (!window.confirm(`Deactivate "${row.original.name}"? It will be hidden from users.`)) return;
		await deactivateProduct(row.original.product_id);
		setIsSearching(true);
	};

	const handleSearchClick = () => {
		setSearchParams((p) => {
			const n = new URLSearchParams(p);
			n.set('name', (p.get('name') || '').trim());
			n.set('fb', (p.get('fb') || '').trim());
			n.set('offset', '0');
			return n;
		}, { replace: true });
		setIsSearching(true);
	};

	const handleClearClick = () => {
		setSearchParams({ }, { replace: true });
		setIsSearching(true);
	};

	const handleImportClick = () => {
		fileInputRef.current.click();
	};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files[0];
		if (file) {
			setIsImporting(true);
			try {
				const summary = await importProducts(file);
				setImportSummary(summary);
				handleSearchClick();
			} finally {
				setIsImporting(false);
				event.target.value = '';
			}
		}
	};

	const SortHeader = ({ label, ascKey, descKey }: { label: string; ascKey: string; descKey: string }) => {
		const isAsc = sortFilter === ascKey;
		const isDesc = sortFilter === descKey;
		const active = isAsc || isDesc;
		return (
			<Tooltip label={isAsc ? 'Sorted ascending — click for descending' : isDesc ? 'Sorted descending — click for ascending' : 'Click to sort'} hasArrow placement="top">
				<HStack
					as="button"
					type="button"
					spacing={1.5}
					cursor="pointer"
					userSelect="none"
					px={2}
					py={1}
					mx={-2}
					borderRadius="md"
					onClick={() => setSortFilter(isAsc ? descKey : ascKey)}
					bg={active ? 'blue.50' : 'transparent'}
					color={active ? 'blue.600' : 'gray.600'}
					borderBottom="2px solid"
					borderColor={active ? 'blue.400' : 'transparent'}
					_hover={{
						bg: 'blue.50',
						color: 'blue.600',
						borderColor: 'blue.200',
					}}
					transition="all 0.15s ease"
				>
					<span>{label}</span>
					{isAsc ? (
						<TriangleUpIcon boxSize={3} color="blue.500" />
					) : isDesc ? (
						<TriangleDownIcon boxSize={3} color="blue.500" />
					) : (
						<UpDownIcon boxSize={3} color="gray.400" />
					)}
				</HStack>
			</Tooltip>
		);
	};

	const columnHelper = createColumnHelper<any>();
	const columns = [
		columnHelper.accessor('product_id', {
			cell: (info) => info.getValue(),
			header: () => <SortHeader label="ID" ascKey="id_asc" descKey="id_desc" />,
		}),
		columnHelper.accessor('name', {
			cell: (info) => info.getValue(),
			header: () => <SortHeader label="NAME" ascKey="name_asc" descKey="name_desc" />,
		}),
		columnHelper.accessor('fb_number', {
			cell: (info) => {
				const val = info.getValue();
				return val ? <Badge colorScheme="purple" fontSize="xs">{val}</Badge> : <Text color="gray.300" fontSize="xs">—</Text>;
			},
			header: 'FB #',
		}),
		columnHelper.accessor('description', {
			cell: (info) => info.getValue(),
			header: 'DESC',
		}),
		columnHelper.accessor('price', {
			cell: (info) => info.getValue(),
			header: () => <SortHeader label="PRICE" ascKey="price_asc" descKey="price_desc" />,
		}),
		columnHelper.accessor('status', {
			cell: (info) => (
				<Badge colorScheme={info.getValue() == 1 ? 'green' : 'red'}>
					{info.getValue() == 1 ? 'Active' : 'Inactive'}
				</Badge>
			),
			header: 'STATUS',
		}),
		columnHelper.accessor('out_of_stock', {
			cell: (info) => {
				const row = info.row.original;
				const qty = row.qty_available;
				return (
					<Box>
						<Badge colorScheme={info.getValue() ? 'red' : 'green'}>
							{info.getValue() ? 'Out of Stock' : 'In Stock'}
						</Badge>
						<Text fontSize="xs" color="gray.500" mt={0.5}>
							FB: {qty == null ? '—' : Number(qty)}
						</Text>
					</Box>
				);
			},
			header: 'STOCK',
		}),
		columnHelper.accessor('action', {
			cell: ({ row }) => (
				<HStack spacing={2}>
					<ButtonGroup size="xs">
						<Tooltip label="Edit" aria-label="Edit tooltip">
							<Button colorScheme="blue" onClick={() => editProduct(row)}>
								<EditIcon />
							</Button>
						</Tooltip>
						<Tooltip label="Price Check" aria-label="Price check tooltip">
							<Button colorScheme="green" onClick={() => handlePriceCheck(row)}>
								<MdAttachMoney />
							</Button>
						</Tooltip>
						<Tooltip label="Stock Check" aria-label="Stock check tooltip">
							<Button colorScheme="purple" onClick={() => handleStockCheck(row)}>
								<MdInventory />
							</Button>
						</Tooltip>
						<Button colorScheme="red" onClick={() => handleDeactivate(row)}>
							<DeleteIcon />
						</Button>
					</ButtonGroup>
				</HStack>
			),
			header: 'ACTION',
		}),
	];

	const table = useReactTable({
		columns,
		data: adminProducts || [],
		getCoreRowModel: getCoreRowModel(),
	});

	useEffect(() => {
		getProducts(offset, nameFilter, statusFilter, fbFilter, categoryFilter, stockFilter, sortFilter).then(() => {
			if (isSearching) setIsSearching(false);
		});
	}, [offset, isSearching, nameFilter, statusFilter, fbFilter, categoryFilter, stockFilter, sortFilter]);

	const Tabs = ({ count, onOffsetChange, offset }) => {
		const groupSize = 25;
		const numPages = Math.ceil(count / groupSize);
		const currentPage = Math.floor(offset / groupSize);

		if (numPages <= 1) return null;

		const pages: (number | 'ellipsis')[] = [0];
		const rangeStart = Math.max(1, currentPage - 1);
		const rangeEnd = Math.min(numPages - 2, currentPage + 1);
		if (rangeStart > 1) pages.push('ellipsis');
		for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
		if (rangeEnd < numPages - 2) pages.push('ellipsis');
		if (numPages > 1) pages.push(numPages - 1);

		return (
			<Flex mt={6} justify="center" align="center" gap={1}>
				<Button size="sm" variant="outline" onClick={() => onOffsetChange(Math.max(0, offset - groupSize))} isDisabled={currentPage === 0}>‹</Button>
				{pages.map((page, idx) =>
					page === 'ellipsis' ? (
						<Text key={`e-${idx}`} px={2} color="gray.400">…</Text>
					) : (
						<Button key={page} size="sm" colorScheme={currentPage === page ? 'blue' : 'gray'} variant={currentPage === page ? 'solid' : 'outline'} onClick={() => onOffsetChange((page as number) * groupSize)}>
							{(page as number) + 1}
						</Button>
					)
				)}
				<Button size="sm" variant="outline" onClick={() => onOffsetChange(Math.min((numPages - 1) * groupSize, offset + groupSize))} isDisabled={currentPage === numPages - 1}>›</Button>
			</Flex>
		);
	};

	return (
		<>
			<Alert />
			<AdminNavbar />

			{/* Price Check Modal */}
			<Modal isOpen={!!priceCheckProduct} onClose={() => setPriceCheckProduct(null)} size="xl" isCentered scrollBehavior="inside">
				<ModalOverlay />
				<ModalContent maxH="80vh">
					<ModalHeader>
						<Text fontSize="md" fontWeight="bold">Price Check</Text>
						<Text fontSize="sm" color="gray.500" fontWeight="normal" mt={0.5} noOfLines={1}>{priceCheckProduct?.name}</Text>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody pb={4}>
						{priceCheckLoading ? (
							<Center py={8}><Spinner color="green.500" /></Center>
						) : (() => {
							const hasVariants = priceCheckData.some((row) => row.variant_id != null);
							return (
							<Table size="sm" variant="simple">
								<Thead>
									<Tr bg="gray.50">
										{hasVariants && <Th fontSize="xs">Variant</Th>}
										<Th fontSize="xs">Facility</Th>
										<Th fontSize="xs">Price Group</Th>
										<Th fontSize="xs" isNumeric>Override</Th>
										<Th fontSize="xs" isNumeric>Final Price</Th>
									</Tr>
								</Thead>
								<Tbody>
									{priceCheckData.map((row, idx) => {
										const hasOverride = row.facility_override != null;
										const hasGroupPrice = row.group_price != null;
										const showVariantLabel = hasVariants && (idx === 0 || priceCheckData[idx - 1].variant_id !== row.variant_id);
										return (
											<Tr key={`${row.variant_id || 'p'}-${row.facilityId}`}>
												{hasVariants && (
													<Td fontSize="sm" fontWeight={showVariantLabel ? 'medium' : 'normal'}>
														{showVariantLabel
															? (row.variant_label || `Variant #${row.variant_id}`)
															: null}
													</Td>
												)}
												<Td fontSize="sm">{row.facility_name}</Td>
												<Td fontSize="sm">
													{row.price_group_name
														? <Badge colorScheme="blue" fontSize="xs">{row.price_group_name}</Badge>
														: <Text color="gray.400" fontSize="xs">—</Text>}
												</Td>
												<Td isNumeric fontSize="sm">
													{hasOverride
														? <Text color="orange.500" fontWeight="medium">${parseFloat(row.facility_override).toFixed(2)}</Text>
														: <Text color="gray.400" fontSize="xs">—</Text>}
												</Td>
												<Td isNumeric>
													<Badge
														colorScheme={hasOverride ? 'orange' : hasGroupPrice ? 'green' : 'gray'}
														fontSize="sm"
														px={2}
													>
														${parseFloat(row.resolved_price).toFixed(2)}
													</Badge>
												</Td>
											</Tr>
										);
									})}
								</Tbody>
							</Table>
							);
						})()}
					</ModalBody>
					<ModalFooter borderTop="1px solid" borderColor="gray.100">
						<HStack fontSize="xs" color="gray.400" spacing={4} flex={1} flexWrap="wrap">
							<HStack><Badge colorScheme="green" fontSize="xs">green</Badge><Text>= price group</Text></HStack>
							<HStack><Badge colorScheme="orange" fontSize="xs">orange</Badge><Text>= facility override</Text></HStack>
							<HStack><Badge colorScheme="gray" fontSize="xs">gray</Badge><Text>= base price</Text></HStack>
						</HStack>
						<Button size="sm" onClick={() => setPriceCheckProduct(null)}>Close</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Stock Check Modal */}
		<Modal isOpen={!!stockCheckProduct} onClose={() => setStockCheckProduct(null)} size="lg" isCentered scrollBehavior="inside">
			<ModalOverlay />
			<ModalContent maxH="80vh">
				<ModalHeader>
					<Text fontSize="md" fontWeight="bold">Stock Check</Text>
					<Text fontSize="sm" color="gray.500" fontWeight="normal" mt={0.5} noOfLines={1}>{stockCheckProduct?.name}</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody pb={4}>
					{stockCheckLoading ? (
						<Center py={8}><Spinner color="purple.500" /></Center>
					) : stockCheckData.length === 0 ? (
						<Stack spacing={3} align="center" py={4}>
							<Badge colorScheme={stockCheckProduct?.out_of_stock ? "red" : "green"} fontSize="sm" px={3} py={1}>
								{stockCheckProduct?.out_of_stock ? "Out of Stock" : "In Stock"}
							</Badge>
							<Box textAlign="center">
								<Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">Fishbowl qty</Text>
								<Text fontSize="lg" fontWeight="semibold" color="gray.800">
									{stockCheckProduct?.qty_available == null ? '—' : Number(stockCheckProduct.qty_available)}
								</Text>
								<Text fontSize="xs" color="gray.400">
									Synced{' '}
									{stockCheckProduct?.qty_synced_at
										? new Date(stockCheckProduct.qty_synced_at).toLocaleString('en-US', {
												month: 'short', day: 'numeric', year: 'numeric',
												hour: '2-digit', minute: '2-digit',
											})
										: '—'}
								</Text>
							</Box>
							{stockCheckProduct?.fb_number && (
								<Text fontSize="xs" color="gray.500">FB #: {stockCheckProduct.fb_number}</Text>
							)}
						</Stack>
					) : (
						<Table size="sm" variant="simple">
							<Thead>
								<Tr bg="gray.50">
									<Th fontSize="xs">Variant</Th>
									<Th fontSize="xs">FB #</Th>
									<Th fontSize="xs" isNumeric>FB Qty</Th>
									<Th fontSize="xs">Status</Th>
									<Th fontSize="xs">Synced</Th>
								</Tr>
							</Thead>
							<Tbody>
								{stockCheckData.map((v) => {
									const outOfStock = stockCheckProduct?.out_of_stock || v.out_of_stock;
									return (
										<Tr key={v.variant_id}>
											<Td fontSize="sm">{v.label || ("Variant #" + v.variant_id)}</Td>
											<Td fontSize="xs" color="gray.600">{v.fb_number || '—'}</Td>
											<Td fontSize="sm" isNumeric fontWeight="medium">
												{v.qty_available == null ? '—' : Number(v.qty_available)}
											</Td>
											<Td>
												<Badge colorScheme={outOfStock ? "red" : "green"} fontSize="xs">
													{outOfStock ? "Out of Stock" : "In Stock"}
												</Badge>
											</Td>
											<Td fontSize="xs" color="gray.500" whiteSpace="nowrap">
												{v.qty_synced_at
													? new Date(v.qty_synced_at).toLocaleString('en-US', {
															month: 'short', day: 'numeric',
															hour: '2-digit', minute: '2-digit',
														})
													: '—'}
											</Td>
										</Tr>
									);
								})}
							</Tbody>
						</Table>
					)}
				</ModalBody>
				<ModalFooter borderTop="1px solid" borderColor="gray.100">
					<Button size="sm" onClick={() => setStockCheckProduct(null)}>Close</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
		{/* Import Summary Modal */}
			<Modal isOpen={!!importSummary} onClose={() => setImportSummary(null)} isCentered>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Import Summary</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Stack spacing={3}>
							<HStack justify="space-between">
								<Text fontWeight="semibold">Successfully Added</Text>
								<Badge colorScheme="green" fontSize="md" px={3} py={1}>{importSummary?.added ?? 0}</Badge>
							</HStack>
							<HStack justify="space-between">
								<Text fontWeight="semibold">Failed / Skipped</Text>
								<Badge colorScheme="red" fontSize="md" px={3} py={1}>{importSummary?.failed ?? 0}</Badge>
							</HStack>
							{importSummary?.errors?.length > 0 && (
								<>
									<Divider />
									<Text fontSize="sm" fontWeight="semibold" color="gray.600">Errors:</Text>
									<Box maxH="200px" overflowY="auto">
										{importSummary.errors.map((err, i) => (
											<Text key={i} fontSize="xs" color="red.500" mb={1}>{err}</Text>
										))}
									</Box>
								</>
							)}
						</Stack>
					</ModalBody>
					<ModalFooter>
						<Button colorScheme="blue" onClick={() => setImportSummary(null)}>Done</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
			{!loading ? (
				<Box pt={{ base: 16, md: 6 }} px={{ base: 4, md: 6 }} pb={6} ml={{ base: 0, md: "250px" }} bg="gray.50" minH="100vh">
					<Heading mb={6} fontSize="2xl" fontWeight="bold" color="gray.700">
						Product List
					</Heading>

					<Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
						<SimpleGrid columns={{ base: 1, md: 5 }} spacing={3} mb={3}>
							<Input
								placeholder="Product Name"
								size="sm"
								borderRadius="md"
								value={nameFilter}
								onChange={(e) => setNameFilter(e.target.value)}
								isDisabled={loading}
							/>
							<Input
								placeholder="FB Number"
								size="sm"
								borderRadius="md"
								value={fbFilter}
								onChange={(e) => setFbFilter(e.target.value)}
								isDisabled={loading}
							/>
						<Select
								placeholder="All Statuses"
								size="sm"
								borderRadius="md"
								value={statusFilter}
								onChange={(e) => setStatusFilter(e.target.value)}
								isDisabled={loading}
							>
								<option value="1">Active</option>
								<option value="0">Inactive</option>
							</Select>
							<Select
								placeholder="All Categories"
								size="sm"
								borderRadius="md"
								value={categoryFilter}
								onChange={(e) => setCategoryFilter(e.target.value)}
								isDisabled={loading}
							>
								{categories.filter((cat) => !cat.parent_id).map((cat) => (
									<option key={cat.category_id} value={cat.category_id}>{cat.category_name}</option>
								))}
							</Select>
							<Select
								placeholder="All Stock"
								size="sm"
								borderRadius="md"
								value={stockFilter}
								onChange={(e) => setStockFilter(e.target.value)}
								isDisabled={loading}
							>
								<option value="0">In Stock</option>
								<option value="1">Out of Stock</option>
							</Select>
						</SimpleGrid>
						<Flex justify="space-between" flexWrap="wrap" gap={2}>
							<HStack spacing={2}>
								<Button
									size="sm"
									colorScheme="teal"
									onClick={handleImportClick}
									leftIcon={<ArrowUpIcon />}
									variant="outline"
									isLoading={isImporting}
									loadingText="Importing..."
									isDisabled={isImporting}
								>
									Import CSV / XLSX
								</Button>
								<Button
									size="sm"
									colorScheme="green"
									onClick={createProd}
									leftIcon={<AddIcon />}
									variant="outline"
									isDisabled={isImporting}
								>
									Create Product
								</Button>
							</HStack>
							<HStack spacing={2}>
								<Button
									size="sm"
									variant="ghost"
									colorScheme="gray"
									onClick={handleClearClick}
									isDisabled={loading}
								>
									Clear Filters
								</Button>
								<Button
									size="sm"
									colorScheme="blue"
									onClick={handleSearchClick}
									isLoading={isSearching}
									leftIcon={<SearchIcon />}
								>
									Search
								</Button>
							</HStack>
						</Flex>
					</Box>

					<input
						type="file"
						ref={fileInputRef}
						onChange={handleFileChange}
						style={{ display: 'none' }}
						accept=".csv,.xlsx,.xls"
					/>

					<Box overflowX="auto" borderRadius="lg" bg="white" boxShadow="md">
						<Table variant="simple" colorScheme="gray" size="sm">
							<Thead bg="gray.100">
								{table.getHeaderGroups().map((headerGroup) => (
									<Tr key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<Th key={header.id}>
												{flexRender(
													header.column.columnDef.header,
													header.getContext()
												)}
											</Th>
										))}
									</Tr>
								))}
							</Thead>
							<Tbody>
								{table.getRowModel().rows.map((row) => (
									<Tr
										key={row.id}
										_hover={{ bg: 'blue.100' }}
										transition="background 0.2s ease"
									>
										{row.getVisibleCells().map((cell) => (
											<Td key={cell.id} fontSize="sm" py={2}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</Td>
										))}
									</Tr>
								))}
							</Tbody>
						</Table>
					</Box>

					<Tabs count={count} onOffsetChange={setOffset} offset={offset} />
				</Box>
			) : (
				<Box ml={{ base: 0, md: "250px" }} p={10} display="flex" justifyContent="center">
					<Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
				</Box>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	adminProducts: state.adminProducts.products,
	loading: state.adminProducts.loading,
	count: state.adminProducts.count,
});

const mapDispatchToProps = {
	getProducts: (offset, name, status, fb_number, category_id, out_of_stock, sort) => getProducts(offset, name, status, fb_number, category_id, out_of_stock, sort),
	importProducts: (file) => importProducts(file),
	deactivateProduct: (id) => deactivateProduct(id),
};

export default connect(mapStateToProps, mapDispatchToProps)(Products);
